import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  apiContextTracker,
  type ApiContextData,
} from "@/services/api/contextTracker";
import {
  isSubResource,
  resourceTypeForOsFocusedApp,
  resourceTypeForPathname,
  scopeActiveResourceToFocus,
} from "@/services/api/activeResourceRelevance";

export type UseApiContextOptions = {
  /** aniOS focused app id — scopes active resources when the router path is generic (e.g. /home). */
  osFocusedApp?: string;
};

/**
 * Hook to access API context data for ANI
 */
export function useApiContext(options: UseApiContextOptions = {}) {
  const { osFocusedApp } = options;
  const [contextHistory, setContextHistory] = useState<ApiContextData[]>([]);
  const location = useLocation();
  const [currentRoute, setCurrentRoute] = useState(location.pathname);

  // Subscribe to context updates
  useEffect(() => {
    // Get initial history
    setContextHistory(apiContextTracker.getContextHistory());

    // Subscribe to updates
    const unsubscribe = apiContextTracker.subscribe((newContext) => {
      setContextHistory(newContext);
    });

    return unsubscribe;
  }, []);

  // Clear history when navigating to a different section
  useEffect(() => {
    const newRoute = location.pathname;

    // Extract main section from routes (e.g., /portfolio/123 -> portfolio)
    const getMainSection = (path: string) => {
      const parts = path.split("/").filter(Boolean);
      return parts[0] || "home";
    };

    const currentSection = getMainSection(currentRoute);
    const newSection = getMainSection(newRoute);

    // Clear history if we've moved to a different main section
    if (currentSection !== newSection) {
      apiContextTracker.clearHistory();
    }

    setCurrentRoute(newRoute);
  }, [location.pathname]);

  // Get recent context (last 5 API calls)
  const recentContext = useMemo(() => {
    return apiContextTracker.getRecentContext(5);
  }, [contextHistory]);

  const focusResourceType = useMemo(() => {
    const fromOs = resourceTypeForOsFocusedApp(osFocusedApp);
    if (fromOs) return fromOs;
    return resourceTypeForPathname(location.pathname);
  }, [osFocusedApp, location.pathname]);

  // Get context for current route
  const routeContext = useMemo(() => {
    if (focusResourceType) {
      return apiContextTracker.getContextByResourceType(focusResourceType);
    }

    return recentContext;
  }, [contextHistory, focusResourceType, recentContext]);

  // Generate context summary for ANI
  const contextSummary = useMemo(() => {
    return apiContextTracker.getContextSummary();
  }, [contextHistory]);

  // Extract active resources - scoped to focus and recent (within 1 minute)
  const activeResources = useMemo(() => {
    const allActiveResources = apiContextTracker.getActiveResources(60 * 1000);

    if (!focusResourceType) {
      return allActiveResources;
    }

    return allActiveResources.filter((resource) =>
      scopeActiveResourceToFocus(resource, focusResourceType),
    );
  }, [contextHistory, focusResourceType]);

  // Get data loaded on current page
  const pageData = useMemo(() => {
    const data: Record<string, unknown> = {};

    routeContext.forEach((ctx) => {
      if (ctx.responseData && ctx.resourceType) {
        data[ctx.resourceType] = ctx.responseData;
      }
    });

    return data;
  }, [routeContext]);

  return {
    contextHistory,
    recentContext,
    routeContext,
    contextSummary,
    activeResources,
    pageData,
    clearHistory: () => apiContextTracker.clearHistory(),
  };
}

export { isSubResource };
